export const CLASS_GRADE_LEVELS = [
  { number: 1, label: "Junior Infants", fields: ["grade_level1", "gradeLevel1"] },
  { number: 2, label: "Senior Infants", fields: ["grade_level2", "gradeLevel2"] },
  { number: 3, label: "1st Class", fields: ["grade_level3", "gradeLevel3"] },
  { number: 4, label: "2nd Class", fields: ["grade_level4", "gradeLevel4"] },
  { number: 5, label: "3rd Class", fields: ["grade_level5", "gradeLevel5"] },
  { number: 6, label: "4th Class", fields: ["grade_level6", "gradeLevel6"] },
  { number: 7, label: "5th Class", fields: ["grade_level7", "gradeLevel7"] },
  { number: 8, label: "6th Class", fields: ["grade_level8", "gradeLevel8"] }
];

const normaliseComparableText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getClassSources = (classItem) =>
  [classItem, classItem?.class].filter((source) => source && typeof source === "object");

const isSelectedGradeFlag = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

const firstPresentValue = (sources, fields) => {
  for (const source of sources) {
    for (const field of fields) {
      const value = source?.[field];

      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
  }

  return "";
};

const getConfiguredGradeLabel = (gradeNumber, countryGrades) =>
  String(
    countryGrades?.[`grade${gradeNumber}`] ||
      countryGrades?.[`grade_level${gradeNumber}`] ||
      ""
  ).trim();

export const getGradeLevelLabel = (value, countryGrades) => {
  const text = String(value || "").trim();
  const gradeNumberMatch = text.match(/^(?:grade[_ ]?level[_ ]?)?(\d)$/i);
  const gradeNumber = gradeNumberMatch ? Number(gradeNumberMatch[1]) : Number.NaN;

  return (
    getConfiguredGradeLabel(gradeNumber, countryGrades) ||
    CLASS_GRADE_LEVELS.find((grade) => grade.number === gradeNumber)?.label ||
    text
  );
};

export const getClassGradeLabel = (classItem, countryGrades) => {
  const sources = getClassSources(classItem);
  const explicitGrade = firstPresentValue(sources, [
    "grade",
    "gradeName",
    "grade_name",
    "gradeLabel",
    "grade_label",
    "gradeLevelLabel",
    "grade_level_label",
    "gradeLevel",
    "grade_level",
    "gradeLevelNumber",
    "grade_level_number"
  ]);

  if (explicitGrade !== "") {
    return getGradeLevelLabel(explicitGrade, countryGrades);
  }

  const flaggedGrade = CLASS_GRADE_LEVELS.find((grade) =>
    sources.some((source) => grade.fields.some((field) => isSelectedGradeFlag(source?.[field])))
  );

  return flaggedGrade
    ? getConfiguredGradeLabel(flaggedGrade.number, countryGrades) || flaggedGrade.label
    : "";
};

export const getClassAcademicYear = (classItem) =>
  String(
    firstPresentValue(getClassSources(classItem), [
      "academicYear",
      "academic_year",
      "academicyear",
      "academicYearLabel",
      "schoolYear",
      "school_year",
      "yearLabel"
    ]) || ""
  ).trim();

export const getClassName = (classItem) =>
  String(
    firstPresentValue([classItem], ["className", "class_name", "classname"]) ||
      firstPresentValue([classItem?.class], ["className", "class_name", "classname", "name"]) ||
      firstPresentValue([classItem], ["name"]) ||
      ""
  ).trim();

export const getDistinctClassGradeLabel = (
  classItem,
  className = getClassName(classItem),
  countryGrades
) => {
  const grade = getClassGradeLabel(classItem, countryGrades);
  return normaliseComparableText(grade) === normaliseComparableText(className) ? "" : grade;
};

export const formatClassDisplayName = (
  classItem,
  fallback = "Class unavailable",
  countryGrades
) => {
  const providedDisplayName = String(
    firstPresentValue(getClassSources(classItem), ["classDisplayName", "class_display_name"]) || ""
  ).trim();
  const className = getClassName(classItem);
  const base = providedDisplayName || className;
  const grade = getDistinctClassGradeLabel(classItem, base || className, countryGrades);
  const academicYear = getClassAcademicYear(classItem);
  const parts = [base];

  [grade, academicYear].forEach((value) => {
    const comparableValue = normaliseComparableText(value);
    const comparableDisplay = normaliseComparableText(parts.join(" "));

    if (value && comparableValue && !comparableDisplay.includes(comparableValue)) {
      parts.push(value);
    }
  });

  return parts.filter(Boolean).join(" - ") || fallback;
};
